import React, { useEffect, useMemo } from 'react'
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import { OrderDetailResponse } from '../../model'
import { getUploadProducts } from '../../helpers/orderUploads'
import { useSuborderUploads } from '../../hooks/useSuborderUploads'
import OrderUploads from './OrderUploads'

const FILES_PER_PAGE = 10

const OrderUploadTab: React.FC = () => {
    const { orderId } = useParams()
    const { order } = useOutletContext<{ order?: OrderDetailResponse }>()
    const [searchParams, setSearchParams] = useSearchParams()
    const orderNumber = orderId ? Number(orderId) : undefined
    const products = useMemo(() => getUploadProducts(order?.products), [order?.products])
    const selectedProductParam = searchParams.get('selectedProduct')
    const requestedProductId = Number(selectedProductParam)
    const selectedProduct = products.find((product) => product.id === requestedProductId)
    const page = Math.max(1, Number(searchParams.get('page')) || 1)

    const setProduct = (productId?: number, replace = false) => {
        const next = new URLSearchParams(searchParams)
        if (productId === undefined) next.delete('selectedProduct')
        else next.set('selectedProduct', String(productId))
        next.delete('page')
        setSearchParams(next, { replace })
    }

    useEffect(() => {
        if (!products.length || !selectedProductParam || selectedProduct) return
        setProduct(undefined, true)
        // The available products and URL determine whether the selection is valid.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, selectedProduct, selectedProductParam])

    const uploads = useSuborderUploads(orderNumber, selectedProduct?.id)
    const total = uploads.files.length
    const totalPages = Math.max(1, Math.ceil(total / FILES_PER_PAGE))
    const currentPage = Math.min(page, totalPages)
    const visibleFiles = uploads.files.slice((currentPage - 1) * FILES_PER_PAGE, currentPage * FILES_PER_PAGE)

    const pageChange = (nextPage: number) => {
        const next = new URLSearchParams(searchParams)
        next.set('page', String(nextPage))
        setSearchParams(next)
    }

    return (
        <OrderUploads
            products={products}
            selectedProductId={selectedProduct?.id}
            onProductChange={setProduct}
            files={visibleFiles}
            totalFiles={total}
            page={currentPage}
            onPageChange={pageChange}
            isLoading={uploads.isLoading}
            isUploading={uploads.isUploading}
            progress={{ current: uploads.uploadCurrent, total: uploads.uploadTotal }}
            message={uploads.message}
            onUpload={uploads.uploadFiles}
            onDownload={uploads.downloadFile}
            onDelete={uploads.deleteFile}
        />
    )
}

export default OrderUploadTab
